import React, { useState, useRef } from 'react';
import { Upload, X } from 'lucide-react';
import { compressImage, formatSize } from '../utils/file';

export const FileDropzone = ({ id, value, onChange, accept = 'image/*,application/pdf', maxSizeMB = 10 }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const files = Array.isArray(value) ? value : (value ? [value] : []);
  const maxBytes = maxSizeMB * 1024 * 1024;

  const handleFiles = async (incoming) => {
    if (!incoming || incoming.length === 0) return;
    setError('');
    setProcessing(true);
    const accepted = [];
    const rejected = [];
    for (const raw of Array.from(incoming)) {
      const compressed = await compressImage(raw);
      if (compressed.size > maxBytes) {
        rejected.push(`${raw.name} (${formatSize(compressed.size)})`);
      } else {
        accepted.push(compressed);
      }
    }
    setProcessing(false);
    if (rejected.length) {
      setError(`File too large (max ${maxSizeMB}MB): ${rejected.join(', ')}`);
    }
    if (accepted.length) onChange([...files, ...accepted]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeAt = (idx) => {
    const next = files.filter((_, i) => i !== idx);
    onChange(next);
    setError('');
  };

  return (
    <div>
      <div
        onClick={() => !processing && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
          dragOver ? 'border-[#FF6600] bg-orange-50' : error ? 'border-red-300 hover:border-red-400' : 'border-gray-300 hover:border-orange-300 hover:bg-gray-50'
        } ${processing ? 'opacity-70 pointer-events-none' : ''}`}
        data-testid={`dropzone-${id}`}
      >
        {processing ? (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
            <span className="inline-block w-4 h-4 border-2 border-[#FF6600] border-t-transparent rounded-full animate-spin" />
            <span>Optimizing image…</span>
          </div>
        ) : files.length > 0 ? (
          <div className="flex flex-col gap-2 text-sm text-gray-700">
            {files.map((f, idx) => (
              <div key={`${f.name}-${idx}`} className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-white border border-gray-200">
                <span className="truncate max-w-[280px] text-left">{f.name}</span>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[11px] text-gray-500">{formatSize(f.size)}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeAt(idx); }}
                    className="text-gray-400 hover:text-red-500"
                    aria-label="Remove file"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            <p className="text-[11px] text-gray-500 mt-1">Click or drag to add more files</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 text-gray-500">
            <Upload className="w-5 h-5" />
            <span className="text-sm">Choose files or drag here</span>
            <span className="text-[11px] text-gray-400">You can upload multiple files (max {maxSizeMB}MB each)</span>
          </div>
        )}
        <input
          ref={inputRef}
          id={id}
          type="file"
          multiple
          accept={accept}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};
